import useCurrentAuthority from 'src/hooks/useCurrentAuthority';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { useIsPrivateBetaWalletWhitelisted } from 'src/hooks/query/useIsPrivateBetaWalletWhitelisted';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';

export interface PrivateBetaWalletWhitelistCheck {
	isPrivateBetaWhitelistEnabled: boolean;
	isEmulatingAccount: boolean;
	connected: boolean;
	authority: string | undefined;
	/** True when the connected wallet should be checked against the whitelist. */
	shouldCheck: boolean;
	isWhitelisted: boolean | undefined;
	isLoading: boolean;
	isFetched: boolean;
	isError: boolean;
}

/**
 * Shared source of truth for the private beta whitelist gate. Resolves the
 * Open Beta env override and feature flag, emulation/connection state and
 * connected authority, then runs the whitelist query when (and only when)
 * every gate precondition is met.
 *
 * Both the connect-time gate (`usePrivateBetaWalletWhitelistGateOnConnect`) and
 * the read-only status hook (`usePrivateBetaWalletWhitelistStatus`) build on
 * this so their predicate and error semantics can never drift apart.
 */
export function usePrivateBetaWalletWhitelistCheck(): PrivateBetaWalletWhitelistCheck {
	const isWhitelistFlagEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_PRIVATE_BETA_WALLET_WHITELIST')
	);
	// Open Beta wins over the flag: no wallet is gated once it is on.
	const isPrivateBetaWhitelistEnabled =
		!Env.enableOpenBeta && isWhitelistFlagEnabled;
	const isEmulatingAccount = useVelocityStore((s) => s.isEmulatingAccount);
	const connected = useWalletIsConnected();
	const authority = useCurrentAuthority()?.toBase58();

	const shouldCheck =
		isPrivateBetaWhitelistEnabled &&
		connected &&
		!isEmulatingAccount &&
		!!authority;

	const {
		data: isWhitelisted,
		isLoading,
		isFetched,
		isError,
	} = useIsPrivateBetaWalletWhitelisted(authority, shouldCheck);

	return {
		isPrivateBetaWhitelistEnabled,
		isEmulatingAccount,
		connected,
		authority,
		shouldCheck,
		isWhitelisted,
		isLoading,
		isFetched,
		isError,
	};
}
